import React from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import SmartProofPreview from "./SmartProofPreview";
import {
  getProofDisplayFileName,
  getProofReviewTitle,
  isValidEmail,
  setStoredEmail,
} from "./proofRowUtils";

function ProofStatusChip({ status }) {
  const normalized = String(status || "").toLowerCase();
  const color = normalized === "approved" ? "success" : normalized === "rejected" ? "error" : "default";
  return <Chip label={status || "Pending"} color={color} size="small" className="proof-review-card__status-chip" />;
}

export default function ProofReviewCard({
  row,
  currentPoId,
  commentValue,
  onChangeComment,
  onChangeClientEmail,
  clientEmail,
  isInternal,
  internalEmail,
  isSubmitting,
  onSubmitDecision,
  onOpenPdf,
  disabled = false,
  readOnly = false,
  requireCommentForChanges = true,
  showInternalApprover = true,
  showClientApprover = true,
  gridClassName = "",
  hideHeader = false,
  previewSx,
  refinedCustomerActions = false,
  labels = {},
}) {
  const { proof, decoration, product } = row || {};
  const pid = proof?.id != null ? String(proof.id) : "";
  const filePath = proof?.filePath || proof?.proofFilePath || "";
  const fileName = getProofDisplayFileName(proof);
  const reviewTitle = getProofReviewTitle({ proof, product, decoration, labels });
  const hasFile = Boolean(filePath);

  const status = String(proof?.status || "").toLowerCase();
  const isFullyApproved = status === "approved";
  const isRejected = status === "rejected";

  const internalState = String(proof?.approvals?.user?.state || "pending").toLowerCase();
  const clientState = String(proof?.approvals?.client?.state || "pending").toLowerCase();
  const roleAlreadyApproved = isInternal ? internalState === "approved" : clientState === "approved";
  const canAct = !readOnly && !disabled && !isRejected && !isFullyApproved && hasFile && !roleAlreadyApproved;

  const rootClassName = ["proof-review-card", gridClassName].filter(Boolean).join(" ");

  const normalizeBy = (by) => {
    if (!by) return { name: "", email: "" };
    if (typeof by === "string") return { name: by, email: "" };
    return { name: by.name || "", email: by.email || "" };
  };

  const userBy = normalizeBy(proof?.approvals?.user?.by);
  const clientBy = normalizeBy(proof?.approvals?.client?.by);
  const clientEmailTrimmed = String(clientEmail || "").trim();
  const clientEmailMissing = !clientEmailTrimmed;
  const clientEmailValid = isValidEmail(clientEmailTrimmed);
  const clientEmailInvalid = Boolean(clientEmailTrimmed && !clientEmailValid);

  const initials = (name, fallback) => {
    const base = (name && String(name)) || fallback;
    return base
      .split(" ")
      .map((part) => part[0])
      .slice(0, 2)
      .join("")
      .toUpperCase();
  };

  const whenText = (at, stateValue) => {
    if (!at) return "-";
    const dt = new Date(at);
    const verb = stateValue === "approved" ? "Approved" : stateValue === "rejected" ? "Rejected" : "Updated";
    return `${verb} at ${dt.toLocaleString()}`;
  };

  const handlePreviewOpen = React.useCallback(
    (src, name) => {
      if (!isSubmitting) onOpenPdf?.(src, name);
    },
    [isSubmitting, onOpenPdf]
  );

  return (
    <Grid item xs={12} className={rootClassName}>
      <Grid container spacing={2} sx={{ p: 2 }} className="proof-review-card__layout">
        <Grid
          item
          xs={12}
          md="auto"
          className="proof-review-card__preview-column"
          sx={{
            flexBasis: { md: 450 + 24 },
            maxWidth: { md: 450 + 24 },
            mr: { md: 3 },
            overflow: "hidden",
          }}
        >
          <Box className="proof-review-card__preview">
            <SmartProofPreview
              filePath={filePath}
              fileName={fileName}
              previewImageUrl={proof?.previewImageUrl || proof?.previewUrl}
              previewStatus={proof?.previewStatus}
              previewKind={proof?.previewKind}
              onPreviewOpen={handlePreviewOpen}
              disabled={disabled || isSubmitting}
              sx={previewSx}
            />
          </Box>
        </Grid>

        <Grid
          item
          xs={12}
          md
          className="proof-review-card__content-column"
          sx={{
            minWidth: { md: 420 },
            flex: "2 1 0",
          }}
        >
          <Box sx={{ position: "relative" }} className="proof-review-card__content">
            {isSubmitting && (
              <Box
                className="proof-review-card__loading-overlay"
                sx={{
                  position: "absolute",
                  inset: 0,
                  bgcolor: "rgba(255,255,255,0.6)",
                  display: "grid",
                  placeItems: "center",
                  zIndex: 2,
                }}
              >
                <CircularProgress size={28} />
              </Box>
            )}

            {!hideHeader ? (
              <Box
                mb={1}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                className="proof-review-card__header"
              >
                <Box className="proof-review-card__header-copy">
                  <Typography variant="subtitle1" className="proof-review-card__title">
                    {reviewTitle}
                  </Typography>
                </Box>

                <Box className="proof-review-card__status">
                  <ProofStatusChip status={proof?.status || "Pending"} />
                </Box>
              </Box>
            ) : null}

            <Box mb={1.5} className="proof-review-card__approvers">
              <Typography
                variant="subtitle2"
                gutterBottom
                className="proof-review-card__section-title proof-review-card__approvers-title"
              >
                {labels.approversLabel || "Approvers"}
              </Typography>

              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                flexWrap="wrap"
                className="proof-review-card__approver-list"
              >
                {showInternalApprover ? (
                  <Stack
                    direction="row"
                    spacing={1.25}
                    alignItems="center"
                    className="proof-review-card__approver proof-review-card__approver--internal"
                  >
                    <Tooltip
                      arrow
                      title={
                        <Box className="proof-review-card__approver-tooltip">
                          <div>
                            <b>{userBy.name || "Internal Reviewer"}</b>
                          </div>
                          <div style={{ opacity: 0.8 }}>{userBy.email || "-"}</div>
                          <div style={{ opacity: 0.8, marginTop: 4 }}>
                            {whenText(proof?.approvals?.user?.at, internalState)}
                          </div>
                        </Box>
                      }
                    >
                      <Avatar
                        sx={{ width: 28, height: 28, fontSize: 13, bgcolor: "#0d47a1" }}
                        alt={userBy.name || "Internal"}
                        className="proof-review-card__approver-avatar proof-review-card__approver-avatar--internal"
                      >
                        {initials(userBy.name, "In")}
                      </Avatar>
                    </Tooltip>
                    <Chip
                      size="small"
                      label={`Internal: ${internalState || "pending"}`}
                      variant="outlined"
                      sx={{ textTransform: "capitalize" }}
                      className="proof-review-card__approver-chip proof-review-card__approver-chip--internal"
                    />
                  </Stack>
                ) : null}

                {showClientApprover ? (
                  <Stack
                    direction="row"
                    spacing={1.25}
                    alignItems="center"
                    className="proof-review-card__approver proof-review-card__approver--client"
                  >
                    <Tooltip
                      arrow
                      title={
                        <Box className="proof-review-card__approver-tooltip">
                          <div>
                            <b>{clientBy.name || "Client Reviewer"}</b>
                          </div>
                          <div style={{ opacity: 0.8 }}>{clientBy.email || "-"}</div>
                          <div style={{ opacity: 0.8, marginTop: 4 }}>
                            {whenText(proof?.approvals?.client?.at, clientState)}
                          </div>
                        </Box>
                      }
                    >
                      <Avatar
                        sx={{ width: 28, height: 28, fontSize: 13, bgcolor: "#1b5e20" }}
                        alt={clientBy.name || "Client"}
                        className="proof-review-card__approver-avatar proof-review-card__approver-avatar--client"
                      >
                        {initials(clientBy.name, "Cl")}
                      </Avatar>
                    </Tooltip>
                    <Chip
                      size="small"
                      label={`Client: ${clientState || "pending"}`}
                      variant="outlined"
                      sx={{ textTransform: "capitalize" }}
                      className="proof-review-card__approver-chip proof-review-card__approver-chip--client"
                    />
                  </Stack>
                ) : null}
              </Stack>
            </Box>

            <Typography
              variant="subtitle2"
              gutterBottom
              className="proof-review-card__section-title proof-review-card__status-title"
            >
              {labels.statusLabel || "Current Status"}
            </Typography>

            <Typography variant="body2" gutterBottom className="proof-review-card__current-status">
              Internal: <b style={{ textTransform: "capitalize" }}>{internalState}</b> &nbsp;•&nbsp; Client:{" "}
              <b style={{ textTransform: "capitalize" }}>{clientState}</b>
            </Typography>

            {isRejected ? (
              <Typography
                variant="caption"
                color="error"
                display="block"
                mt={2}
                className="proof-review-card__rejected-message"
              >
                This proof has been rejected. Further actions are disabled until manual intervention.
              </Typography>
            ) : null}

            {!isRejected && !isFullyApproved ? (
              <>
                {isInternal ? (
                  <TextField
                    className="proof-review-card__email-field proof-review-card__email-field--internal"
                    label={labels.internalEmailLabel || "Reviewer email"}
                    type="email"
                    fullWidth
                    sx={{
                      mb: 1.5,
                      ...(refinedCustomerActions ? {
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: "#EEE",
                          borderRadius: 1,
                        },
                      } : {}),
                    }}
                    value={internalEmail || ""}
                    disabled
                  />
                ) : (
                  <TextField
                    className="proof-review-card__email-field proof-review-card__email-field--client"
                    label={labels.clientEmailLabel || "Your email (for approval record)"}
                    type="email"
                    fullWidth
                    sx={{
                      mb: 1.5,
                      ...(refinedCustomerActions ? {
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: "#EEE",
                          borderRadius: 1,
                        },
                      } : {}),
                    }}
                    value={clientEmail}
                    onChange={(event) => {
                      const nextValue = event.target.value;
                      onChangeClientEmail?.(pid, nextValue);
                      if (isValidEmail(nextValue)) setStoredEmail(nextValue);
                    }}
                    disabled={isSubmitting || disabled || readOnly}
                    required
                    error={clientEmailInvalid}
                    helperText={
                      clientEmailMissing
                        ? "Email is required to record this proof decision."
                        : clientEmailInvalid
                          ? "Enter a valid email (e.g., name@company.com)"
                          : " "
                    }
                  />
                )}

                <TextField
                  className="proof-review-card__comment-field"
                  label={labels.commentLabel || "Comments (optional)"}
                  placeholder={
                    hasFile
                      ? "Tell us what to tweak, or confirm it is good to go."
                      : "No proof attached for this row."
                  }
                  multiline
                  minRows={3}
                  fullWidth
                  value={commentValue}
                  onChange={(event) => onChangeComment?.(pid, event.target.value)}
                  variant="outlined"
                  disabled={isSubmitting || !hasFile || !canAct || (!isInternal && !clientEmailValid)}
                  InputProps={{
                    sx: {
                      bgcolor: hasFile ? "#EEE" : "#F5F5F5",
                      borderRadius: refinedCustomerActions ? 1 : 2,
                      "& textarea": {
                        fontFamily:
                          '"Nunito", system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial',
                      },
                    },
                  }}
                />

                <Box
                  display="flex"
                  gap={2}
                  mt={2}
                  flexWrap="wrap"
                  className="proof-review-card__actions"
                >
                  <Button
                    className="proof-review-card__action proof-review-card__action--approve"
                    variant="contained"
                    startIcon={refinedCustomerActions ? <CheckCircleOutlineIcon /> : undefined}
                    onClick={() => onSubmitDecision?.(currentPoId, pid, "approved")}
                    disabled={isSubmitting || !hasFile || !canAct || (!isInternal && !clientEmailValid)}
                    sx={refinedCustomerActions ? {
                      flex: "1 1 150px",
                      minHeight: 44,
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 800,
                      fontSize: 14,
                      background: "linear-gradient(180deg, #2f9955 0%, #238244 100%)",
                      boxShadow: "0 4px 10px rgba(35, 130, 68, 0.16)",
                      "&:hover": {
                        background: "linear-gradient(180deg, #2b8f50 0%, #1f763e 100%)",
                        boxShadow: "0 6px 12px rgba(35, 130, 68, 0.20)",
                      },
                      "&.Mui-disabled": {
                        color: "#fff",
                        background: "#a7d7b4",
                        opacity: 0.8,
                      },
                    } : {
                      textTransform: "none",
                      fontWeight: 700,
                      bgcolor: "#469553",
                      "&:hover": { bgcolor: "#ea580c" },
                      "&.Mui-disabled": { bgcolor: "action.disabledBackground" },
                    }}
                  >
                    {labels.approveLabel || "Approve"}
                  </Button>

                  <Button
                    className="proof-review-card__action proof-review-card__action--request-changes"
                    variant="contained"
                    startIcon={refinedCustomerActions ? <EditNoteOutlinedIcon /> : undefined}
                    onClick={() => onSubmitDecision?.(currentPoId, pid, "rejected")}
                    disabled={
                      isSubmitting ||
                      !hasFile ||
                      !canAct ||
                      (requireCommentForChanges && !commentValue?.trim()) ||
                      (!isInternal && !clientEmailValid)
                    }
                    sx={refinedCustomerActions ? {
                      flex: "1 1 150px",
                      minHeight: 44,
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 700,
                      fontSize: 14,
                      color: "#fff",
                      background: "#f97316",
                      boxShadow: "none",
                      "&:hover": {
                        background: "#ea580c",
                        boxShadow: "none",
                      },
                      "&.Mui-disabled": {
                        color: "#fff",
                        background: "#fdba74",
                        opacity: 0.8,
                      },
                    } : {
                      textTransform: "none",
                      fontWeight: 700,
                      bgcolor: "#f97316",
                      "&:hover": { bgcolor: "#ea580c" },
                      "&.Mui-disabled": { bgcolor: "action.disabledBackground" },
                    }}
                  >
                    {labels.requestChangesLabel || "Request Changes"}
                  </Button>
                </Box>
              </>
            ) : null}

            {isFullyApproved ? (
              <Typography
                variant="caption"
                color="success.main"
                display="block"
                mt={2}
                className="proof-review-card__approved-message"
              >
                This proof has been fully approved.
              </Typography>
            ) : null}
          </Box>
        </Grid>
      </Grid>
    </Grid>
  );
}
